import { useCallback, useMemo, useEffect } from 'react';
import {
  setPeriodStart,
  setPeriodEnd,
} from 'redux/reducers/meteorologyAgroCultureIndexAreaReducer';
import {
  format,
  parse,
} from 'date-fns';
import { store } from 'redux/store';
import { CultureIndexAreaMap } from 'types/Meteorology/Agro/CultureIndex/Area/map';
import { useLazyGetCultureIndexForecastPeriodQuery } from 'services/api/Meteorology/Agro/CultureIndex';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import useForecastDate from './useForecastDate';

export default function useForecastPeriod(map: CultureIndexAreaMap) {
  const { forecastDate } = useForecastDate(map);
  const dateString = format(forecastDate, 'yyyy-MM-dd');

  const [trigger, {
    data, isFetching, error,
  }] = useLazyGetCultureIndexForecastPeriodQuery();

  useHandleRequestErrors(error as ResponseError);

  useEffect(() => {
    if (dateString) trigger({ forecastDate: dateString });
  }, [dateString, trigger]);

  const availablePeriod = useMemo(() => {
    if (!data) return [];
    return data.map((date) => parse(date, 'yyyy-MM-dd', new Date()));
  }, [data]);

  const changePeriod = useCallback((newDates: (Date | null)[]) => {
    const [newStartDate, newEndDate] = newDates;
    const newPeriodStart = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : undefined;
    const newPeriodEnd = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStart({
      id: map.id,
      periodStart: newPeriodStart,
    }));
    store.dispatch(setPeriodEnd({
      id: map.id,
      periodEnd: newPeriodEnd,
    }));
  }, [map]);

  const periodStart = map.params.periodStart
    ? parse(map.params.periodStart, 'yyyy-MM-dd', new Date())
    : null;

  const periodEnd = map.params.periodEnd
    ? parse(map.params.periodEnd, 'yyyy-MM-dd', new Date())
    : null;

  return {
    availablePeriod,
    changePeriod,
    periodStart,
    periodEnd,
    isFetching,
  };
}
